import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControlStatus, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { FormStatus, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import fastDeepEqual from 'fast-deep-equal';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ImprovedAccountMappingTemplate, SpecialFLIs } from '../../helpers/datev.excel.helper';

@UntilDestroy()
@Component({
    selector: 'ar-special-fli-settings-form',
    templateUrl: 'special-fli-settings.component.html',
    styleUrls: ['special-fli-settings.component.scss'],
})
export class TemplateSpecialFlisFormComponent implements OnInit {
    public formGroup: UntypedFormGroup | undefined;
    @Input() public canUseUncovershorfall: boolean;
    @Input() canUseEquity = false;
    @Input() public template: ImprovedAccountMappingTemplate | undefined;

    @Input() groupOptions: INameId[] = [];
    @Input() fliOptions: INameId[] = [];
    @Input() assetsFliOptions: INameId[] = [];
    @Input() liaFliOptions: INameId[] = [];
    @Output() templateSpecialFlisFormComplete: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() specialFlisSelections: EventEmitter<SpecialFLIs> = new EventEmitter<SpecialFLIs>();

    constructor() {
        this.initializeForm();
    }

    ngOnInit(): void {
        this.updateFormWithInitialData();
        this.monitorFormChanges();
    }

    private initializeForm(): void {
        this.formGroup = new UntypedFormGroup({
            equityGroup: new UntypedFormControl(),
            profitFLI: new UntypedFormControl(),
            uncoveredShortfallAssetsFli: new UntypedFormControl(),
            uncoveredShortfallLiabilitiesFli: new UntypedFormControl(),
            annualSurplusFli: new UntypedFormControl(),
        });
    }

    private updateFormWithInitialData(): void {
        let uncoveredShortfallAssetsFli: string = undefined;
        let uncoveredShortfallLiabilitiesFli: string = undefined;
        if (this.template != null) {
            if (this.canUseUncovershorfall) {
                uncoveredShortfallAssetsFli = this.template.uncoveredShortfallAssetsFli;
                uncoveredShortfallLiabilitiesFli = this.template.uncoveredShortfallLiabilitiesFli;
            }
            this.formGroup?.patchValue({
                equityGroup: { id: this.template.equityGroupName },
                profitFLI: { id: this.template.profitOfTheYear },
                uncoveredShortfallAssetsFli: { id: uncoveredShortfallAssetsFli },
                uncoveredShortfallLiabilitiesFli: { id: uncoveredShortfallLiabilitiesFli },
                annualSurplusFli: { id: this.template.annualSurplusFli },
            });
        }
    }

    private monitorFormChanges(): void {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined.');
        }
        this.formGroup.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.handleFormChanges());
        this.formGroup.statusChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((status: FormControlStatus) =>
                this.templateSpecialFlisFormComplete.emit(status === FormStatus.VALID),
            );
    }

    private handleFormChanges(): void {
        if (!this.formGroup.valid) {
            return;
        }
        const equityGroupNameId: string | undefined = this.formGroup?.get('equityGroup')?.value?.id;
        const profitOfTheYearId: string | undefined = this.formGroup?.get('profitFLI')?.value?.id;
        const uncoverShortFallAssetsId: string | undefined =
            this.formGroup?.get('uncoveredShortfallAssetsFli')?.value?.id;
        const uncoverShortFallLiabilitiesId: string | undefined = this.formGroup?.get(
            'uncoveredShortfallLiabilitiesFli',
        )?.value?.id;
        const annualSurplusId: string | undefined = this.formGroup?.get('annualSurplusFli')?.value?.id;

        this.specialFlisSelections.emit({
            equityGroupNameId,
            profitOfTheYearId,
            uncoverShortFallAssetsId,
            uncoverShortFallLiabilitiesId,
            annualSurplusId,
        });
    }
}
